/**
 * The legacy Klaviyo "Added to Cart" event, restored.
 *
 * The previous theme sent this from snippets/addtocartevent.liquid. It did not survive
 * the migration, so the live flow "Abandonment Added-to-Cart [V99]" — which listens on
 * the legacy API metric, not the native Shopify one — stopped receiving events on
 * 16 September 2026. Klaviyo's own Shopify integration keeps sending the native metric;
 * this file only restores the legacy producer beside it.
 *
 * Not a port of the old snippet. That one patched window.fetch to sniff /cart/add.js,
 * read a global the Klaviyo app happens to define, tracked on the button's click rather
 * than on a successful add, and latched a flag that let one event through per page. Here
 * the event follows the theme's own `biosphere:cart:change`, which is published only
 * after the platform has accepted an add, and carries the added line with it.
 *
 * The payload is the legacy one, field for field, because live emails read those exact
 * keys: `Name`, `ImageURL`, `URL`, `Price` and `Value` are rendered in the first two,
 * and `Value` is what the discount arithmetic multiplies.
 *
 * Nothing here may break an add. Every path is wrapped, and a failure to track is
 * swallowed — the buyer's cart is not the place to surface an analytics fault.
 */

const METRIC = "Added to Cart";

/**
 * Shopify's consent API, where the storefront has one. Absent (older sessions, or the
 * API still loading) is treated as permitted, which is what the legacy snippet did;
 * Klaviyo then applies its own consent rules to what it receives, as before.
 */
function trackingAllowed() {
  try {
    const privacy = window.Shopify?.customerPrivacy;
    if (typeof privacy?.analyticsProcessingAllowed !== "function") return true;
    return privacy.analyticsProcessingAllowed();
  } catch {
    return true;
  }
}

/** Emails render these as `<img src>` and `<a href>`, so a path would not resolve. */
function absoluteUrl(value) {
  if (!value) return "";
  try {
    return new URL(value, window.location.origin).href;
  } catch {
    return String(value);
  }
}

/**
 * Money as the legacy event carried it: a formatted string for display (`$89.99`) and
 * a bare numeric string for arithmetic (`89.99`). Liquid formats money everywhere else
 * in this theme, but a cart line arrives from the platform as minor units and the
 * event has to state both forms.
 */
function formatMoney(minorUnits, currency) {
  const major = (Number(minorUnits) || 0) / 100;
  try {
    return new Intl.NumberFormat(document.documentElement.lang || "en-NZ", {
      style: "currency",
      currency: currency || "NZD",
      currencyDisplay: "narrowSymbol",
    }).format(major);
  } catch {
    return `$${major.toFixed(2)}`;
  }
}

function majorUnits(minorUnits) {
  return ((Number(minorUnits) || 0) / 100).toFixed(2);
}

/**
 * The product half of the payload.
 *
 * On a product page the Klaviyo app embed publishes `window.item` — the same global the
 * old snippet merged in — so when it describes the product just added, it is used as
 * is. That keeps the restored event byte-identical to the historical one on the path
 * that produced nearly all of them.
 *
 * Everywhere else (landing pages, the drawer's pairing) it is built from the added line.
 * `Categories` is the one field with no client-side source: it is the product's
 * collection titles, which the cart does not carry. Left out rather than guessed at —
 * an invented value would be worse than an absent one for anything segmenting on it.
 */
function productFields(line, currency) {
  const pageItem = window.item;
  const sameProduct =
    pageItem &&
    typeof pageItem === "object" &&
    Number(pageItem.ProductID) === Number(line.product_id);

  if (sameProduct) {
    /* Identity, brand, collections and compare-at from the embed; money from the line.
       The embed states the product's one-time price, so a subscription add would
       otherwise report $69.99 for a $59.49 line — and `Value` is what the email's
       discount arithmetic multiplies. */
    return {
      ...pageItem,
      ImageURL: absoluteUrl(pageItem.ImageURL),
      Price: formatMoney(line.price, currency),
      Value: majorUnits(line.price),
    };
  }

  return {
    Name: line.product_title || line.title || "",
    ProductID: line.product_id,
    ImageURL: absoluteUrl(line.image),
    URL: absoluteUrl(String(line.url || "").split("?")[0]),
    Brand: line.vendor || "",
    Price: formatMoney(line.price, currency),
    Value: majorUnits(line.price),
    CompareAtPrice: formatMoney(line.original_price ?? line.price, currency),
  };
}

/**
 * `total_discount` is passed through in the platform's minor units, as the legacy
 * snippet sent it — the other totals are majors. It is wrong-looking and deliberate:
 * whatever reads it downstream was built against that shape.
 */
function cartFields(cart) {
  return {
    total_price: (Number(cart.total_price) || 0) / 100,
    $value: (Number(cart.total_price) || 0) / 100,
    total_discount: cart.total_discount,
    original_total_price: (Number(cart.original_total_price) || 0) / 100,
    items: cart.items ?? [],
  };
}

/**
 * The line this add put in the cart.
 *
 * The publishers pass the platform's add response, which names the item exactly — the
 * cart alone cannot, since a second add may land between the two requests. But the two
 * add endpoints disagree about money: `/cart/add.js` answers in minor units as integers
 * (`6999`) while `/cart/add` with a JSON accept header answers in major units as strings
 * (`"69.99"`), which is how the pairing add reached Klaviyo claiming $0.70.
 *
 * So the response identifies the line and the cart snapshot supplies it. The snapshot is
 * one shape, always minor units, and is the state the rest of the payload describes.
 */
function addedLine(detail) {
  const { item, cart } = detail;
  const lines = cart?.items ?? [];

  const key = item?.key;
  if (key) {
    const byKey = lines.find((line) => line.key === key);
    if (byKey) return byKey;
  }

  const variantId = Number(item?.variant_id ?? detail.variantId);
  if (variantId) {
    const byVariant = lines.find(
      (line) => Number(line.variant_id) === variantId,
    );
    if (byVariant) return byVariant;
  }

  /* Cart fetch failed or the line has already gone: the response is all there is. */
  return item?.product_id ? item : null;
}

function track(event) {
  const detail = event.detail ?? {};
  if (detail.action !== "add") return;
  if (!trackingAllowed()) return;

  const cart = detail.cart;
  if (!cart) return;

  const line = addedLine(detail);
  if (!line) return;

  const klaviyo = (window._learnq = window._learnq || []);
  klaviyo.push([
    "track",
    METRIC,
    { ...cartFields(cart), ...productFields(line, cart.currency) },
  ]);
}

document.addEventListener("biosphere:cart:change", (event) => {
  try {
    track(event);
  } catch {
    /* Tracking never interrupts a purchase. */
  }
});
